import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Trash2, Download, CheckCircle2, Clock, AlertCircle, Loader } from 'lucide-react';
import { formatFileSize } from '../../services/storage';
import ExpiryBadge from './ExpiryBadge';

import { cn } from '../../lib/utils';

export default function DocumentRow({ document, onDelete, projectId, isSelected, onSelect, isDeleting }) {
  const [showMenu, setShowMenu] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      uploaded: {
        icon: Clock,
        label: 'Uploaded',
        class: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700'
      },
      analyzing: {
        icon: Loader,
        label: 'Analyzing',
        class: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700',
        animate: true
      },
      analyzed: {
        icon: CheckCircle2,
        label: 'Analyzed',
        class: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-700'
      },
      processing: {
        icon: Loader,
        label: 'Processing',
        class: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-700',
        animate: true
      },
      processed: {
        icon: CheckCircle2,
        label: 'Processed',
        class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700'
      },
      error: {
        icon: AlertCircle,
        label: 'Error',
        class: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-700'
      }
    };

    return configs[status] || configs.uploaded;
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${document.name}"?`)) {
      onDelete(document.id, projectId);
    }
  };

  const statusConfig = getStatusConfig(document.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className={cn(
      "flex items-center gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition border-b border-gray-200 dark:border-dark-border last:border-b-0 group",
      isDeleting && "opacity-50 pointer-events-none"
    )}>
      {/* Checkbox */}
      <div className="flex-shrink-0">
        <input
          type="checkbox"
          checked={isSelected || false}
          onChange={(e) => onSelect && onSelect(document.id, e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-slate-700"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Status Icon */}
      <div className="flex-shrink-0">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig.class.split(' ')[0]} dark:bg-opacity-30`}>
          <StatusIcon className={`w-5 h-5 ${statusConfig.class.includes('text-') ? statusConfig.class.split(' ').find(c => c.startsWith('text-')) : 'text-gray-600'} ${statusConfig.animate ? 'animate-spin' : ''}`} />
        </div>
      </div>

      {/* Filename & Date */}
      <Link to={`/documents/${document.id}`} className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 dark:text-white truncate hover:text-emerald-600 dark:hover:text-emerald-400">
          {document.name}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {formatDate(document.createdAt)}
        </p>
      </Link>

      {/* Pages */}
      <div className="flex-shrink-0 text-sm text-gray-600 dark:text-gray-400 min-w-[60px]">
        {document.pageCount || document.analysis?.totalPages || '-'} {(document.pageCount || document.analysis?.totalPages) === 1 ? 'pg' : 'pgs'}
      </div>

      {/* Size */}
      <div className="flex-shrink-0 text-sm text-gray-600 dark:text-gray-400 min-w-[80px]">
        {formatFileSize(document.fileSize || 0)}
      </div>

      {/* Status Badge */}
      <div className="flex-shrink-0">
        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${statusConfig.class}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* Expiry Badge */}
      <div className="flex-shrink-0">
        <ExpiryBadge createdAt={document.createdAt} />
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
        {document.downloadUrl && (
          <a
            href={document.downloadUrl}
            download
            onClick={(e) => e.stopPropagation()}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
            title="Download"
          >
            <Download className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </a>
        )}
        <button
          onClick={handleDelete}
          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
          title="Delete"
        >
          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
        </button>
      </div>
    </div>
  );
}
